import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ApiError, get, post, postForm, withQuery } from "./client";

/** `/api/v1/crew`: the crew's day, a site, its status and photos, the prep
 * pack and the way into the checklist.
 *
 * Reads want a signal; writes do not. A status move or a prep tick that fails
 * to send is queued in `localStorage` and replayed through the sync
 * endpoints when the tablet is back online, the same contract the Jinja
 * screens' `crew.js` and `prep.js` kept. The prep pack itself is cached
 * per site as it is fetched, so the yard's copy still opens in the chamber. */

export interface CrewSiteRow {
  id: number;
  name: string;
  address: string | null;
  status: string;
  status_label: string;
  priority: number | null;
  planned_date: string | null;
  valve_make: string | null;
  dn_scope: number | null;
}

export interface CrewDay {
  /** The provider's name, for the heading: "My work · Skakuza Crew A". */
  crew: string | null;
  today: string;
  days: string[];
  selected: string | null;
  stops: { seq: number; site: CrewSiteRow; arrive: string; depart: string; outcome: string }[];
  sites: CrewSiteRow[];
  points: { id: number; name: string; lat: number; lon: number; status: string }[];
  unplaceable: number;
}

export interface DeferralReason {
  code: string;
  label: string;
  /** "Other" must say why, or the report of failed visits says nothing. */
  needs_note: boolean;
}

export interface CrewSite extends CrewSiteRow {
  rs_no: string | null;
  notif_no: string | null;
  func_loc: string | null;
  equip_no: string | null;
  valve_desc: string | null;
  station_type: string | null;
  lat: number | null;
  lon: number | null;
  allowed_next: { value: string; label: string }[];
  photos: { id: number; url: string; caption: string | null }[];
  qcp_url: string | null;
  prep_pdf_url: string;
  open_inspection_id: number | null;
  can_start_inspection: boolean;
  /** The last attempt that could not go ahead, while it still stands. */
  deferred: { at: string | null; reason: string; note: string | null } | null;
  deferral_reasons: DeferralReason[];
}

export interface PackLine {
  key: string;
  text: string;
  source: string;
  because: string | null;
  done: boolean;
  answer: string | null;
  detail: string | null;
}

export interface PrepPack {
  site: CrewSiteRow;
  sections: { section: string; title: string; notice: string | null; lines: PackLine[] }[];
  plan: {
    supplier: { id: number; name: string } | null;
    suggestions: {
      part_id: number;
      description: string;
      item_code: string | null;
      size: string;
      role: string;
      variant: string;
      certain: boolean;
    }[];
    question: string | null;
    unavailable: string | null;
  };
  pdf_url: string;
  fetched_at: string;
}

export const CREW_KEY = ["crew"] as const;
export const crewSiteKey = (id: number) => ["crew", "sites", id] as const;
export const packKey = (id: number) => ["crew", "sites", id, "prep"] as const;

export function useCrewDay(day: string | null) {
  return useQuery<CrewDay, ApiError>({
    queryKey: [...CREW_KEY, "day", day],
    queryFn: () => get<CrewDay>(withQuery("/api/v1/crew", { day })),
  });
}

export function useCrewSite(id: number | null) {
  return useQuery<CrewSite, ApiError>({
    queryKey: crewSiteKey(id ?? 0),
    queryFn: () => get<CrewSite>(`/api/v1/crew/sites/${id}`),
    enabled: id !== null,
  });
}

function useCrewSiteWrite<TVars>(id: number, write: (vars: TVars) => Promise<CrewSite>) {
  const client = useQueryClient();
  return useMutation<CrewSite, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (site) => {
      client.setQueryData(crewSiteKey(id), site);
      void client.invalidateQueries({ queryKey: [...CREW_KEY, "day"] });
    },
  });
}

export function useUploadCrewPhoto(id: number) {
  return useCrewSiteWrite(id, (file: File) => {
    const form = new FormData();
    form.append("photo", file, file.name);
    return postForm<CrewSite>(`/api/v1/crew/sites/${id}/photo`, form);
  });
}

/** "Couldn't do it today": the attempt and its reason. Online only -- the
 * offline queue replays status moves, and a deferral is not one; the screen
 * says so rather than pretending it was kept. */
export function useDeferVisit(id: number) {
  return useCrewSiteWrite(id, (body: { reason: string; note: string | null }) =>
    post<CrewSite>(`/api/v1/crew/sites/${id}/defer`, body),
  );
}

export function useStartSiteInspection(id: number) {
  const client = useQueryClient();
  return useMutation<{ inspection_id: number; created: boolean }, ApiError, void>({
    mutationFn: () => post<{ inspection_id: number; created: boolean }>(`/api/v1/crew/sites/${id}/inspection`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: crewSiteKey(id) });
      void client.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}

// --- the offline queues ---------------------------------------------------------

export const STATUS_QUEUE_KEY = "prv_crew_queue_v1";
export const PREP_QUEUE_KEY = "prv_prep_queue_v1";
export const PACK_CACHE_KEY = "prv_prep_pack_v1";

export interface QueuedStatus {
  client_id: string;
  site_id: number;
  new_status: string;
  note: string | null;
}

export interface QueuedTick {
  site_id: number;
  item_key: string;
  done: boolean;
  answer: string | null;
}

/** `localStorage` behind a tiny interface, so the queue can be tested with a
 * plain object and survives a browser that refuses storage (private mode). */
export interface Store {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

let store: Store | null = null;

/** One store for the page: `localStorage` when the browser allows it, memory
 * when it does not, so hooks can depend on it without re-registering. */
export function browserStore(): Store {
  if (store === null) {
    try {
      store = window.localStorage;
    } catch {
      const memory = new Map<string, string>();
      store = { getItem: (k) => memory.get(k) ?? null, setItem: (k, v) => void memory.set(k, v) };
    }
  }
  return store;
}

export function readQueue<T>(store: Store, key: string): T[] {
  try {
    const raw = store.getItem(key);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

export function writeJson(store: Store, key: string, value: unknown): void {
  try {
    store.setItem(key, JSON.stringify(value));
  } catch {
    // Storage refused; the queue lives in memory for this page only.
  }
}

export function writeQueue<T>(store: Store, key: string, queue: T[]): void {
  writeJson(store, key, queue);
}

export function clientId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** A tick replaces any earlier queued tick on the same line: the server
 * upserts, so only the last word matters and replaying both would be noise. */
export function withTick(queue: QueuedTick[], tick: QueuedTick): QueuedTick[] {
  return [...queue.filter((q) => !(q.site_id === tick.site_id && q.item_key === tick.item_key)), tick];
}

/** Apply a queued tick to a cached pack, so the screen shows the tick the
 * crew made even though the server has not heard of it yet. */
export function packWithTick(pack: PrepPack, tick: QueuedTick): PrepPack {
  return {
    ...pack,
    sections: pack.sections.map((section) => ({
      ...section,
      lines: section.lines.map((line) => (line.key === tick.item_key ? { ...line, done: tick.done, answer: tick.answer ?? line.answer } : line)),
    })),
  };
}

export function readPackCache(store: Store): Record<string, PrepPack> {
  try {
    const raw = store.getItem(PACK_CACHE_KEY);
    const parsed: unknown = raw === null ? {} : JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, PrepPack>) : {};
  } catch {
    return {};
  }
}

export function cachePack(store: Store, pack: PrepPack, siteId: number): void {
  writeJson(store, PACK_CACHE_KEY, { ...readPackCache(store), [String(siteId)]: pack });
}

/** The prep pack: from the server when it answers, from the cache when it
 * does not, with the queued ticks laid over either. */
export function usePrepPack(siteId: number | null) {
  const store = browserStore();
  return useQuery<PrepPack, ApiError>({
    queryKey: packKey(siteId ?? 0),
    enabled: siteId !== null,
    queryFn: async () => {
      const id = siteId ?? 0;
      let pack: PrepPack;
      try {
        pack = await get<PrepPack>(`/api/v1/crew/sites/${id}/prep`);
        cachePack(store, pack, id);
      } catch (error) {
        const cached = readPackCache(store)[String(id)];
        if (cached === undefined || (error instanceof ApiError && error.status < 500)) {
          throw error;
        }
        pack = cached;
      }
      return readQueue<QueuedTick>(store, PREP_QUEUE_KEY)
        .filter((tick) => tick.site_id === id)
        .reduce(packWithTick, pack);
    },
  });
}

/** Tick a line now if the network allows, otherwise queue it; either way the
 * cached pack shows the tick at once. */
export function useTickPrep(siteId: number) {
  const client = useQueryClient();
  const store = browserStore();
  return useMutation<{ queued: boolean }, ApiError, { item_key: string; done: boolean; answer: string | null }>({
    mutationFn: async (vars) => {
      const tick: QueuedTick = { site_id: siteId, ...vars };
      client.setQueryData<PrepPack>(packKey(siteId), (pack) => (pack ? packWithTick(pack, tick) : pack));
      try {
        await post(`/api/v1/crew/sites/${siteId}/prep`, vars);
        return { queued: false };
      } catch (error) {
        if (error instanceof ApiError && error.status < 500) {
          throw error;
        }
        writeQueue(store, PREP_QUEUE_KEY, withTick(readQueue<QueuedTick>(store, PREP_QUEUE_KEY), tick));
        return { queued: true };
      }
    },
  });
}

/** Move a site now if the network allows, otherwise queue the move. */
export function useCrewStatus(siteId: number) {
  const client = useQueryClient();
  const store = browserStore();
  return useMutation<{ queued: boolean; site: CrewSite | null }, ApiError, { new_status: string; note: string | null }>({
    mutationFn: async (vars) => {
      try {
        const site = await post<CrewSite>(`/api/v1/crew/sites/${siteId}/status`, vars);
        client.setQueryData(crewSiteKey(siteId), site);
        void client.invalidateQueries({ queryKey: [...CREW_KEY, "day"] });
        return { queued: false, site };
      } catch (error) {
        if (error instanceof ApiError && error.status < 500) {
          throw error;
        }
        const queue = readQueue<QueuedStatus>(store, STATUS_QUEUE_KEY);
        writeQueue(store, STATUS_QUEUE_KEY, [...queue, { client_id: clientId(), site_id: siteId, ...vars }]);
        return { queued: true, site: null };
      }
    },
  });
}

export interface SyncResult {
  client_id: string;
  ok: boolean;
  status?: string;
  error?: string;
}

/** Replay both queues. Status entries the server applied are dropped; those
 * it refused for a real reason stay, so the crew sees them still pending
 * rather than losing the update. Ticks are replayed per site and cleared
 * once the server has the pack as it now stands. */
export async function flushQueues(store: Store): Promise<{ applied: number; pending: number; refused: SyncResult[] }> {
  let applied = 0;
  const refused: SyncResult[] = [];
  const statuses = readQueue<QueuedStatus>(store, STATUS_QUEUE_KEY);
  if (statuses.length > 0) {
    const body = await post<{ results: SyncResult[] }>("/api/v1/crew/sync", { items: statuses });
    const ok = new Set(body.results.filter((r) => r.ok).map((r) => r.client_id));
    applied += ok.size;
    refused.push(...body.results.filter((r) => !r.ok));
    writeQueue(
      store,
      STATUS_QUEUE_KEY,
      statuses.filter((s) => !ok.has(s.client_id)),
    );
  }
  const ticks = readQueue<QueuedTick>(store, PREP_QUEUE_KEY);
  const bySite = new Map<number, QueuedTick[]>();
  for (const tick of ticks) {
    bySite.set(tick.site_id, [...(bySite.get(tick.site_id) ?? []), tick]);
  }
  for (const [siteId, items] of bySite) {
    const pack = await post<PrepPack>(`/api/v1/crew/sites/${siteId}/prep/sync`, {
      items: items.map(({ item_key, done, answer }) => ({ item_key, done, answer })),
    });
    cachePack(store, pack, siteId);
    applied += items.length;
    writeQueue(
      store,
      PREP_QUEUE_KEY,
      readQueue<QueuedTick>(store, PREP_QUEUE_KEY).filter((t) => t.site_id !== siteId),
    );
  }
  return { applied, pending: readQueue(store, STATUS_QUEUE_KEY).length + readQueue(store, PREP_QUEUE_KEY).length, refused };
}

export function pendingCount(store: Store): number {
  return readQueue(store, STATUS_QUEUE_KEY).length + readQueue(store, PREP_QUEUE_KEY).length;
}

/** How many writes are waiting for a signal, and a flush on reconnect and on
 * mount. The count is what the crew sees in the shell badge. */
export function useOfflineQueue() {
  const client = useQueryClient();
  const store = browserStore();
  const [pending, setPending] = useState(() => pendingCount(store));
  const [lastRefusals, setRefusals] = useState<SyncResult[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function flush() {
      if (pendingCount(store) === 0) {
        return;
      }
      try {
        const result = await flushQueues(store);
        if (cancelled) {
          return;
        }
        setRefusals(result.refused);
        void client.invalidateQueries({ queryKey: CREW_KEY });
      } catch {
        // Still offline; the next reconnect tries again.
      }
      if (!cancelled) {
        setPending(pendingCount(store));
      }
    }
    void flush();
    const onLine = () => void flush();
    const onStorage = () => setPending(pendingCount(store));
    window.addEventListener("online", onLine);
    window.addEventListener("storage", onStorage);
    const timer = window.setInterval(() => setPending(pendingCount(store)), 2000);
    return () => {
      cancelled = true;
      window.removeEventListener("online", onLine);
      window.removeEventListener("storage", onStorage);
      window.clearInterval(timer);
    };
  }, [client, store]);

  return { pending, refusals: lastRefusals, flush: () => flushQueues(store).then(() => setPending(pendingCount(store))) };
}
